import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, MessageSquare, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/modules/auth';
import { BrandLogo } from '@/components/BrandLogo';
import { cn } from '@/lib/utils';

/* ─── Animation Config ──────────────────────────────────────────────────── */

const ease = [0.22, 1, 0.36, 1] as const;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1.0, ease },
};

const fadeUpView = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 1.0, ease, delay },
});

/* ─── Data ──────────────────────────────────────────────────────────────── */

const diagramSteps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Request',
    description: 'Tenant submits a request. Your AI agent takes it from here.',
  },
  {
    number: '02',
    icon: Zap,
    title: 'AI Analysis',
    description: 'AI triages the issue, classifies the trade, and estimates cost.',
  },
  {
    number: '03',
    icon: FileCheck,
    title: 'Quotes',
    description: 'AI coordinates verified tradies who compete with real pricing.',
  },
  {
    number: '04',
    icon: ArrowRight,
    title: 'Approval',
    description: 'Owner approves the best quote. AI handles the rest.',
  },
];

const roadmapPhases = [
  {
    label: 'AI Maintenance Workforce',
    description: 'Your AI maintenance coordinator. Receives requests, triages issues, coordinates tradies, manages quotes, and handles approvals.',
    active: true,
  },
  {
    label: 'AI Operations Layer',
    description: 'AI admin and compliance. Deadline tracking, automated reminders, documentation preparation.',
    active: false,
  },
  {
    label: 'Predictive Property Intelligence',
    description: 'AI that predicts maintenance issues before they happen. Cost benchmarking across portfolios. Asset performance optimisation.',
    active: false,
  },
];

// TODO: Replace with validated metrics or remove before launch — these are placeholder estimates
const metrics = [
  { value: '15+', unit: 'hours', label: 'saved per week on admin' },
  { value: '< 2', unit: 'min', label: 'average AI response time' },
  { value: '500+', unit: 'properties', label: 'managed by AI agents' },
];

// TODO: Replace with real testimonials before launch — these are placeholder copy
const testimonials = [
  {
    content:
      'Managing 50+ properties used to be chaos. Now tenants submit requests, tradies compete with quotes, and I just pick the best one.',
    name: 'Sarah M.',
    role: 'Real Estate Agent, Sydney',
  },
  {
    content:
      'We cut our maintenance admin time by 80%. The competitive quoting means we always get fair prices for landlords.',
    name: 'James R.',
    role: 'Agency Director',
  },
  {
    content:
      'I browse jobs in my area, submit competitive quotes, and win work based on my reputation. Fair and transparent.',
    name: 'Mike T.',
    role: 'Licensed Electrician',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

const LandingPage: React.FC = () => {
  const { user, userRole } = useAuth();
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getDashboardLink = () => {
    if (!user) return '/auth';
    switch (userRole) {
      case 'tradie':
        return '/tradie';
      case 'admin':
        return '/admin';
      case 'property_manager':
        return '/pm';
      default:
        return '/dashboard';
    }
  };

  const signupLink = '/auth?mode=signup&role=property_manager';

  return (
    <div className="min-h-screen bg-white">
      {/* ── 1. Navigation ─────────────────────────────────────────────── */}
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 bg-white transition-[border-color] duration-300',
          navScrolled ? 'border-b border-border' : 'border-b border-transparent'
        )}
      >
        <div className="landing-container h-16 flex items-center justify-between">
          <Link to="/">
            <BrandLogo size="sm" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="#how-it-works"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              How It Works
            </a>
            <Link
              to="/pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Pricing
            </Link>
            <a
              href="#platform"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Roadmap
            </a>
            <a
              href="#results"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Results
            </a>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <Button asChild variant="default" size="sm">
                <Link to={getDashboardLink()}>Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/auth?mode=login">Sign In</Link>
                </Button>
                <Button asChild variant="default" size="sm">
                  <Link to="/pricing">Get Started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── 2. Hero ───────────────────────────────────────────────────── */}
      <section className="min-h-screen flex items-center justify-center pt-16">
        <div className="landing-container py-32 md:py-40 lg:py-48 text-center">
          <motion.h1
            {...fadeUp}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-foreground leading-[1.05]"
          >
            The AI workforce for
            <br />
            <span className="text-gradient">property maintenance.</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            Tenant requests, AI estimates, tradie quotes, and owner approvals
            — AI agents that do the work, not just track it.
          </motion.p>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-4 text-sm md:text-base text-muted-foreground/70 max-w-xl mx-auto"
          >
            The AI workforce for property management.
            <br className="hidden sm:block" />
            Your first AI employee starts with maintenance.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.4 }}
            className="mt-14"
          >
            <Button asChild variant="default" size="xl">
              <Link to={user ? getDashboardLink() : '/pricing'}>
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ── 3. System Diagram — How It Works ──────────────────────────── */}
      <section id="how-it-works" className="landing-section bg-white">
        <div className="landing-container">
          <motion.p
            {...fadeUpView()}
            className="landing-label text-center mb-4"
          >
            How It Works
          </motion.p>

          <motion.h2
            {...fadeUpView(0.1)}
            className="landing-heading text-center mb-20"
          >
            Four steps. Zero chaos.
          </motion.h2>

          <div className="relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-border" />
            <div className="md:hidden absolute left-10 top-[56px] bottom-[56px] w-px bg-border" />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-0">
              {diagramSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  {...fadeUpView(index * 0.15)}
                  className="relative flex md:flex-col items-start md:items-center text-left md:text-center px-0 md:px-4"
                >
                  <div className="relative z-10 w-20 h-20 rounded-full border-2 border-border bg-white flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-8 h-8 text-accent" />
                  </div>

                  {index < diagramSteps.length - 1 && (
                    <div className="hidden md:block absolute top-[38px] -right-1 w-2 h-2 rounded-full bg-accent z-20" />
                  )}

                  <div className="ml-6 md:ml-0 md:mt-8">
                    <span className="landing-label">{step.number}</span>
                    <h3 className="text-lg font-semibold text-foreground mt-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[200px] md:mx-auto">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Platform Roadmap ───────────────────────────────────────── */}
      <section id="platform" className="landing-section bg-background">
        <div className="landing-container">
          <motion.p
            {...fadeUpView()}
            className="landing-label text-center mb-4"
          >
            Product Roadmap
          </motion.p>

          <motion.h2
            {...fadeUpView(0.1)}
            className="landing-heading text-center mb-6"
          >
            Your AI workforce, built in phases.
          </motion.h2>

          <motion.p
            {...fadeUpView(0.15)}
            className="text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-20"
          >
            Humans manage relationships. AI executes operations.
          </motion.p>

          {/* Desktop: horizontal roadmap */}
          <div className="hidden md:block">
            {/* Connecting line */}
            <div className="relative">
              <div className="absolute top-3 left-[16.67%] right-[16.67%] h-px bg-border" />

              <div className="grid grid-cols-3 gap-0">
                {roadmapPhases.map((phase, index) => (
                  <motion.div
                    key={index}
                    {...fadeUpView(index * 0.1)}
                    className="relative flex flex-col items-center text-center px-4"
                  >
                    {/* Node */}
                    <div
                      className={cn(
                        'relative z-10 w-6 h-6 rounded-full border-2 flex-shrink-0',
                        phase.active
                          ? 'bg-accent border-accent'
                          : 'bg-white border-border'
                      )}
                    />

                    {/* Label */}
                    <div className="mt-6">
                      <span
                        className={cn(
                          'text-xs uppercase tracking-widest',
                          phase.active ? 'text-accent font-medium' : 'text-muted-foreground/50'
                        )}
                      >
                        Phase {index + 1}
                      </span>
                      <p
                        className={cn(
                          'text-sm font-medium mt-1',
                          phase.active
                            ? 'text-foreground'
                            : 'text-muted-foreground/60'
                        )}
                      >
                        {phase.label}
                      </p>
                      <p
                        className={cn(
                          'text-xs mt-2 max-w-[220px] mx-auto leading-relaxed',
                          phase.active
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/40'
                        )}
                      >
                        {phase.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile: vertical roadmap */}
          <div className="md:hidden">
            <div className="relative pl-8">
              {/* Vertical line */}
              <div className="absolute left-[11px] top-0 bottom-0 w-px bg-border" />

              <div className="space-y-10">
                {roadmapPhases.map((phase, index) => (
                  <motion.div
                    key={index}
                    {...fadeUpView(index * 0.1)}
                    className="relative flex items-start gap-5"
                  >
                    {/* Node */}
                    <div
                      className={cn(
                        'absolute -left-8 top-0.5 w-6 h-6 rounded-full border-2 flex-shrink-0 z-10',
                        phase.active
                          ? 'bg-accent border-accent'
                          : 'bg-white border-border'
                      )}
                    />

                    {/* Label */}
                    <div>
                      <span
                        className={cn(
                          'text-xs uppercase tracking-widest',
                          phase.active ? 'text-accent font-medium' : 'text-muted-foreground/50'
                        )}
                      >
                        Phase {index + 1}
                      </span>
                      <p
                        className={cn(
                          'text-sm font-medium mt-0.5',
                          phase.active
                            ? 'text-foreground'
                            : 'text-muted-foreground/60'
                        )}
                      >
                        {phase.label}
                      </p>
                      <p
                        className={cn(
                          'text-xs mt-1 leading-relaxed',
                          phase.active
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/40'
                        )}
                      >
                        {phase.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Value Proposition — Metrics ────────────────────────────── */}
      <section id="results" className="landing-section bg-white">
        <div className="landing-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                {...fadeUpView(index * 0.1)}
                className="text-center"
              >
                <div className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-foreground">
                  {metric.value}
                </div>
                <div className="text-lg md:text-xl font-medium text-accent mt-2">
                  {metric.unit}
                </div>
                <div className="text-sm text-muted-foreground mt-2 max-w-[200px] mx-auto">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Social Proof — Rotating Testimonial ────────────────────── */}
      <section className="landing-section bg-background">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="landing-label mb-16">What people are saying</p>

          <div className="relative min-h-[240px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6, ease }}
                className="absolute inset-0 flex flex-col items-center justify-center"
              >
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-light italic text-foreground leading-relaxed">
                  &ldquo;{testimonials[activeTestimonial].content}&rdquo;
                </blockquote>
                <div className="mt-8">
                  <div className="text-sm font-semibold text-foreground">
                    {testimonials[activeTestimonial].name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {testimonials[activeTestimonial].role}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-2 mt-12">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  index === activeTestimonial
                    ? 'bg-accent w-6'
                    : 'bg-border w-2 hover:bg-muted-foreground/30'
                )}
                aria-label={`Show testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Footer CTA + Footer ────────────────────────────────────── */}
      <footer className="bg-primary text-white">
        <div className="landing-container py-24 md:py-32 text-center">
          <motion.h2
            {...fadeUpView()}
            className="text-4xl md:text-5xl font-semibold tracking-tight text-white"
          >
            Ready to hire your AI maintenance team?
          </motion.h2>

          <motion.p
            {...fadeUpView(0.1)}
            className="mt-6 text-lg text-white/60 max-w-xl mx-auto"
          >
            Your AI workforce is ready. No credit card required.
          </motion.p>

          <motion.div
            {...fadeUpView(0.2)}
            className="mt-10"
          >
            <Button
              asChild
              size="xl"
              className="bg-accent text-white hover:bg-accent/90 shadow-lg"
            >
              <Link to={user ? getDashboardLink() : '/pricing'}>
                Get Started
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="border-t border-white/10">
          <div className="landing-container py-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <BrandLogo size="sm" variant="white" showText={false} />

            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link
                to="/auth?mode=signup&role=tradie"
                className="hover:text-white/70 transition-colors"
              >
                Join as a Tradie
              </Link>
              <Link to="/privacy" className="hover:text-white/70 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-white/70 transition-colors">
                Terms
              </Link>
            </div>

            <div className="text-sm text-white/30">
              &copy; {new Date().getFullYear()} Agentive Property
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
